import React, { useState, useEffect } from "react";
import { useSubscription, gql } from "@apollo/client";
import styles from "./style.module.scss";
import { WinTAXData, ProcessedWinTAXData } from "../../../types/wintax";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";

const BLUE_CAR_INBOX_HEADPHONE_VOLUME_PRESET = -3;
const BLUE_CAR_INBOX_MIC_VOLUME_PRESET = 0;
const RED_CAR_INBOX_HEADPHONE_VOLUME_PRESET = -3;
const RED_CAR_INBOX_MIC_VOLUME_PRESET = 0;

const WIN_TAX_SUBSCRIPTION = gql`
  subscription WinTAXUpdate {
    winTaxUpdate {
      redCar {
        SA_CurVol_inbox_headphone_vol
        SA_CurVol_inbox_mic_vol
      }
      blueCar {
        SA_CurVol_inbox_headphone_vol
        SA_CurVol_inbox_mic_vol
      }
    }
  }
`;

const PROCESSED_WIN_TAX_SUBSCRIPTION = gql`
  subscription ProcessedWinTAXUpdate {
    processedWinTAXUpdate {
      redCar {
        dt
        gpsQuality
        sessionEnergyCharged
      }
      blueCar {
        dt
        gpsQuality
        sessionEnergyCharged
      }
    }
  }
`;

interface CarPitData {
  SA_CurVol_inbox_headphone_vol: Partial<WinTAXData>["SA_CurVol_inbox_headphone_vol"];
  SA_CurVol_inbox_mic_vol: Partial<WinTAXData>["SA_CurVol_inbox_mic_vol"];
  sessionEnergyCharged: Partial<ProcessedWinTAXData>["sessionEnergyCharged"];
  lastUpdate?: number;
}

interface PitWidgetState {
  redCar: CarPitData;
  blueCar: CarPitData;
}

function WinTAXSummary() {
  const [carData, setCarData] = useState<PitWidgetState>({
    redCar: {
      SA_CurVol_inbox_headphone_vol: undefined,
      SA_CurVol_inbox_mic_vol: undefined,
      sessionEnergyCharged: undefined,
      lastUpdate: undefined,
    },
    blueCar: {
      SA_CurVol_inbox_headphone_vol: undefined,
      SA_CurVol_inbox_mic_vol: undefined,
      sessionEnergyCharged: undefined,
      lastUpdate: undefined,
    },
  });

  const strategyImport = useSelector(
    (state: RootState) => state.imports?.imports.strategy,
    isEqual,
  );

  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isDataFresh = (lastUpdate?: number) => {
    if (!lastUpdate) return false;
    return currentTime - lastUpdate < 5000; // 5 seconds threshold
  };

  // Raw WinTAX subscription
  const { error: rawError } = useSubscription(WIN_TAX_SUBSCRIPTION, {
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const update = subscriptionData?.data?.winTaxUpdate;
      if (!update) return;

      setCarData((prevData) => ({
        redCar: {
          ...prevData.redCar,
          SA_CurVol_inbox_headphone_vol:
            update.redCar?.SA_CurVol_inbox_headphone_vol ??
            prevData.redCar.SA_CurVol_inbox_headphone_vol,
          SA_CurVol_inbox_mic_vol:
            update.redCar?.SA_CurVol_inbox_mic_vol ??
            prevData.redCar.SA_CurVol_inbox_mic_vol,
          lastUpdate: update.redCar ? Date.now() : prevData.redCar.lastUpdate,
        },
        blueCar: {
          ...prevData.blueCar,
          SA_CurVol_inbox_headphone_vol:
            update.blueCar?.SA_CurVol_inbox_headphone_vol ??
            prevData.blueCar.SA_CurVol_inbox_headphone_vol,
          SA_CurVol_inbox_mic_vol:
            update.blueCar?.SA_CurVol_inbox_mic_vol ??
            prevData.blueCar.SA_CurVol_inbox_mic_vol,
          lastUpdate: update.blueCar ? Date.now() : prevData.blueCar.lastUpdate,
        },
      }));
    },
  });

  // Processed WinTAX subscription
  const { loading, error: processedError } = useSubscription(
    PROCESSED_WIN_TAX_SUBSCRIPTION,
    {
      shouldResubscribe: true,
      onSubscriptionData: ({ subscriptionData }) => {
        const update = subscriptionData?.data?.processedWinTAXUpdate;
        if (!update) return;

        setCarData((prevData) => ({
          redCar: {
            ...prevData.redCar,
            sessionEnergyCharged:
              update.redCar?.sessionEnergyCharged ??
              prevData.redCar.sessionEnergyCharged,
            lastUpdate: update.redCar ? Date.now() : prevData.redCar.lastUpdate,
          },
          blueCar: {
            ...prevData.blueCar,
            sessionEnergyCharged:
              update.blueCar?.sessionEnergyCharged ??
              prevData.blueCar.sessionEnergyCharged,
            lastUpdate: update.blueCar
              ? Date.now()
              : prevData.blueCar.lastUpdate,
          },
        }));
      },
    },
  );

  if (processedError || rawError) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p>Error: {processedError?.message || rawError?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingMessage}>
            <div className={styles.spinner}></div>
            WinTAX Data Unavailable
          </div>
        </div>
      )}
      <div className={styles.carGrid}>
        {/* Blue Car Box */}
        <div className={`${styles.carBox} ${styles.blueCar}`}>
          <div className={styles.titleRow}>
            <h4>Blue Car</h4>
            <div
              className={`${styles.statusIndicator} ${
                isDataFresh(carData.blueCar.lastUpdate)
                  ? styles.active
                  : styles.inactive
              }`}
            />
          </div>
          <div className={styles.metrics}>
            <div className={styles.metric}>
              <label data-tooltip="Headphone Vol Inbox">
                {strategyImport?.data?.rUmbilicalHeadphoneVolumeSrcLabel ||
                  "Headphone Vol Inbox"}
                :
              </label>
              <span>
                {carData.blueCar.SA_CurVol_inbox_headphone_vol !== undefined
                  ? (
                      carData.blueCar.SA_CurVol_inbox_headphone_vol -
                      BLUE_CAR_INBOX_HEADPHONE_VOLUME_PRESET
                    ).toFixed(0)
                  : "N/A"}
              </span>
            </div>
            <div className={styles.metric}>
              <label data-tooltip="Mic Vol Inbox">
                {strategyImport?.data?.rUmbilicalMicVolumeSrcLabel ||
                  "Mic Vol Inbox"}
                :
              </label>
              <span>
                {carData.blueCar.SA_CurVol_inbox_mic_vol !== undefined
                  ? (
                      carData.blueCar.SA_CurVol_inbox_mic_vol -
                      BLUE_CAR_INBOX_MIC_VOLUME_PRESET
                    ).toFixed(0)
                  : "N/A"}
              </span>
            </div>
            <div className={styles.metric}>
              <label>Session Energy Charged:</label>
              <span>
                {carData.blueCar.sessionEnergyCharged?.toFixed(2) || "N/A"} kWh
              </span>
            </div>
          </div>
        </div>

        {/* Red Car Box */}
        <div className={`${styles.carBox} ${styles.redCar}`}>
          <div className={styles.titleRow}>
            <h4>Red Car</h4>
            <div
              className={`${styles.statusIndicator} ${
                isDataFresh(carData.redCar.lastUpdate)
                  ? styles.active
                  : styles.inactive
              }`}
            />
          </div>
          <div className={styles.metrics}>
            <div className={styles.metric}>
              <label data-tooltip="Headphone Vol Inbox">
                {strategyImport?.data?.rUmbilicalHeadphoneVolumeSrcLabel ||
                  "Headphone Vol Inbox"}
                :
              </label>
              <span>
                {carData.redCar.SA_CurVol_inbox_headphone_vol !== undefined
                  ? (
                      carData.redCar.SA_CurVol_inbox_headphone_vol -
                      RED_CAR_INBOX_HEADPHONE_VOLUME_PRESET
                    ).toFixed(0)
                  : "N/A"}
              </span>
            </div>
            <div className={styles.metric}>
              <label data-tooltip="Mic Vol Inbox">
                {strategyImport?.data?.rUmbilicalMicVolumeSrcLabel ||
                  "Mic Vol Inbox"}
                :
              </label>
              <span>
                {carData.redCar.SA_CurVol_inbox_mic_vol !== undefined
                  ? (
                      carData.redCar.SA_CurVol_inbox_mic_vol -
                      RED_CAR_INBOX_MIC_VOLUME_PRESET
                    ).toFixed(0)
                  : "N/A"}
              </span>
            </div>
            <div className={styles.metric}>
              <label>Session Energy Charged:</label>
              <span>
                {carData.redCar.sessionEnergyCharged?.toFixed(2) || "N/A"} kWh
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WinTAXSummary;
